/**
 * AircraftOverlay - Renders the Expanding Card and action buttons above selected aircraft
 *
 * Performance-optimized: position is updated imperatively via style.transform
 * in the shared RAF loop. React only re-renders when selection or data changes.
 *
 * Shows:
 * - ExpandingCard (compact/expanded states) - positioned via 3D→2D projection
 * - Collision warning
 * - Save and Revert buttons - Only visible when position/rotation has changed
 *
 * The card is driven by aircraftCardStore (state machine) and synced
 * with aircraftEditorStore via useAircraftCardSync hook.
 */

import { useEffect, useMemo, useRef } from 'react'
import { Save, RotateCcw, AlertTriangle } from 'lucide-react'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useCollisionStore } from '@/stores/collisionStore'
import { Button } from '@/components/ui/button'
import { enrichDetectionWithNames } from '@/lib/utils/format-aircraft-label'
import { ExpandingCard } from './expanding-card'
import { useAircraftCardSync } from '@/hooks/useAircraftCardSync'
import { useCardKeyboardShortcuts } from '@/hooks/useCardKeyboardShortcuts'
import type { PlacedAircraft3D } from '@/services/3d'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import type { LabelPositionData } from '@/hooks/useMapLabelPositions'

interface AircraftOverlayProps {
  aircraftData: PlacedAircraft3D[]
  enrichedData: Map<string, AllocationEnrichedData>
  buildings: Building3DConfigExtended[]
  onSaveClick?: (allocationId: string) => void
  onRevertClick?: (allocationId: string) => void
  onFrame: (callback: (positions: LabelPositionData) => void) => () => void
}

export function AircraftOverlay({
  aircraftData,
  enrichedData,
  buildings,
  onSaveClick,
  onRevertClick,
  onFrame,
}: AircraftOverlayProps) {
  // Bridge editor store → card store
  useAircraftCardSync()
  // Keyboard shortcuts (Esc, 1-4 tabs)
  useCardKeyboardShortcuts()

  const selectedAllocationId = useAircraftEditorStore(
    (state) => state.selectedAllocationId
  )
  const isDirty = useAircraftEditorStore((state) =>
    state.selectedAllocationId ? state.isDirty(state.selectedAllocationId) : false
  )
  const isDragging = useAircraftEditorStore((state) => state.isDragging)
  const isRotating = useAircraftEditorStore((state) => state.isRotating)
  const editedAllocation = useAircraftEditorStore((state) =>
    state.selectedAllocationId
      ? state.getEditedAllocation(state.selectedAllocationId)
      : undefined
  )
  const currentDetection = useAircraftEditorStore((state) =>
    state.selectedAllocationId
      ? state.currentDetections.get(state.selectedAllocationId)
      : undefined
  )

  // Card state
  const cardMode = useAircraftCardStore((state) => state.cardState.mode)

  // Collision state for selected aircraft
  const collisionDetails = useCollisionStore((state) =>
    selectedAllocationId ? state.getCollisionDetails(selectedAllocationId) : undefined
  )

  // Get enriched data for selected aircraft
  const enrichedDataForSelected = selectedAllocationId
    ? enrichedData.get(selectedAllocationId)
    : undefined

  // Build collision warning message
  const collisionMessage = useMemo(() => {
    if (!collisionDetails) return null

    if (collisionDetails.collisionType === 'wall' || collisionDetails.collisionType === 'door') {
      const typeLabel = collisionDetails.collisionType === 'door' ? 'Door' : 'Wall'
      const sideLabel = collisionDetails.wallSide
        ? collisionDetails.wallSide.charAt(0).toUpperCase() + collisionDetails.wallSide.slice(1)
        : 'Unknown'
      return `${typeLabel}: ${sideLabel} side, Unit ${collisionDetails.unitId || 'Unknown'}`
    }

    if (collisionDetails.collisionType === 'aircraft') {
      const names = collisionDetails.collidingAircraftIds
        ?.map((id) => aircraftData.find((a) => a.id === id)?.tailNumber)
        .filter(Boolean)
        .join(', ')
      return `Aircraft: ${names || 'Unknown'}`
    }

    return null
  }, [collisionDetails, aircraftData])

  // Enrich detection results with human-readable names
  const { original, current } = useMemo(() => {
    if (!editedAllocation || !buildings) {
      return { original: null, current: null }
    }

    const originalEnriched = enrichDetectionWithNames(
      {
        areaId: editedAllocation.originalAreaId,
        unitId: editedAllocation.originalUnitId,
        positionType: editedAllocation.originalPositionType,
      },
      editedAllocation.originalPosition,
      buildings
    )

    const currentEnriched = currentDetection
      ? enrichDetectionWithNames(
          currentDetection,
          editedAllocation.currentPosition,
          buildings
        )
      : originalEnriched

    return { original: originalEnriched, current: currentEnriched }
  }, [editedAllocation, currentDetection, buildings])

  // Ref for the overlay wrapper — used for imperative positioning
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Track latest screen X for ExpandingCard positioning
  const screenXRef = useRef(0)

  // Subscribe to the shared RAF position loop for selected aircraft
  useEffect(() => {
    if (!selectedAllocationId) return

    const unsubscribe = onFrame((positions: LabelPositionData) => {
      const el = wrapperRef.current
      if (!el) return

      const pos = positions.aircraft.get(selectedAllocationId)
      if (!pos || !pos.visible) {
        el.style.display = 'none'
        return
      }

      el.style.display = ''
      el.style.left = `${pos.x}px`
      el.style.top = `${pos.y}px`
      screenXRef.current = pos.x
    })

    return unsubscribe
  }, [onFrame, selectedAllocationId])

  // Don't render if no selection
  if (!selectedAllocationId) {
    return null
  }

  // Hide during drag/rotate for cleaner interaction
  const isInteracting = isDragging || isRotating

  return (
    <div
      ref={wrapperRef}
      className={`absolute z-20 pointer-events-auto transition-opacity duration-150 ${
        isInteracting ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{
        transform: 'translate(-50%, -100%)',
      }}
    >
      {/* Position info overlay (during editing) */}
      {editedAllocation && original && current && (
        <div className="mb-2 text-center space-y-1">
          {/* Collision warning */}
          {collisionMessage && (
            <div className="px-2 py-1 text-[10px] bg-red-100/95 border border-red-300 rounded shadow-sm">
              <div className="flex items-center gap-1 text-red-700 font-medium">
                <AlertTriangle className="w-3 h-3" />
                <span>Collision</span>
              </div>
              <div className="text-red-600 mt-0.5">{collisionMessage}</div>
            </div>
          )}
        </div>
      )}

      {/* Expanding Card - shows automatically when cardMode is compact/expanded */}
      {enrichedDataForSelected && (cardMode === 'compact' || cardMode === 'expanded') && (
        <ExpandingCard data={enrichedDataForSelected} screenX={screenXRef.current} />
      )}

      {/* Save/Revert bar (when dirty) */}
      {isDirty && (
        <div className="flex gap-1 p-1 mt-2 bg-background/95 rounded-lg shadow-md">
          <Button
            size="sm"
            variant="default"
            className="h-8 px-3 bg-green-600 hover:bg-green-700"
            onClick={() => onSaveClick?.(selectedAllocationId)}
          >
            <Save className="w-4 h-4 mr-1" />
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-3"
            onClick={() => onRevertClick?.(selectedAllocationId)}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Revert
          </Button>
        </div>
      )}
    </div>
  )
}
